import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from './firebase';
import './MonthEndProcessing.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function MonthEndProcessing({ onClose }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-11
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [royaltyData, setRoyaltyData] = useState(null);
  const [previousSubmissions, setPreviousSubmissions] = useState([]);
  const [franchiseeInfo, setFranchiseeInfo] = useState(null);
  const [courierFees, setCourierFees] = useState(0);
  const [courierDescription, setCourierDescription] = useState('');
  const [otherAmount, setOtherAmount] = useState(0);
  const [otherDescription, setOtherDescription] = useState('');
  const [tshirtOrders, setTshirtOrders] = useState(0);
  const [stickerOrdersAmount, setStickerOrdersAmount] = useState(0);
  const [hoCharges, setHoCharges] = useState([]); // Charges added by HO
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    loadMonthEndData();
  }, [selectedMonth, selectedYear]);

  const loadMonthEndData = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      // Load franchisee info
      const franchiseeDoc = await getDoc(franchiseeRef);
      if (franchiseeDoc.exists()) {
        setFranchiseeInfo(franchiseeDoc.data());
      }

      // Load all students for this franchisee
      const studentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      const studentsSnapshot = await getDocs(studentsQuery);
      
      const studentsList = [];
      
      for (const studentDoc of studentsSnapshot.docs) {
        const studentData = studentDoc.data();
        
        const termPayments = studentData.termPayments || {};
        
        const termBreakdown = {
          term1: termPayments.term1 || 0,
          term2: termPayments.term2 || 0,
          term3: termPayments.term3 || 0,
          term4: termPayments.term4 || 0,
          admin: termPayments.admin || 0
        };
        
        const totalTermFees = termBreakdown.term1 + termBreakdown.term2 + 
                              termBreakdown.term3 + termBreakdown.term4;
        const totalAdminFee = termBreakdown.admin;
        
        console.log('Student:', studentData.studentName, 'Term Payments:', termBreakdown);
        
        if (totalTermFees > 0 || totalAdminFee > 0) {
          studentsList.push({
            id: studentDoc.id,
            ...studentData,
            termBreakdown,
            totalAdminFee,
            totalTermFees
          });
        }
      }

      studentsList.sort((a, b) => {
        const schoolA = a.schoolName || '';
        const schoolB = b.schoolName || '';
        if (schoolA !== schoolB) return schoolA.localeCompare(schoolB);
        return (a.studentName || '').localeCompare(b.studentName || '');
      });

      console.log('Final students list:', studentsList);
      setStudents(studentsList);
      
      const submissions = await loadPreviousSubmissions(franchiseeRef);
      calculateRoyalties(studentsList, submissions);
      await loadTshirtOrders(franchiseeRef);
      await loadHOCharges(user.uid);
      
    } catch (error) {
      console.error('Error loading month-end data:', error);
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTshirtOrders = async (franchiseeRef) => {
    try {
      const user = auth.currentUser;
      console.log('Loading T-shirt orders for user:', user.uid);
      console.log('Franchisee ref path:', franchiseeRef.path);
      
      const ordersQuery = query(
        collection(db, 'tshirtOrders'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      
      const snapshot = await getDocs(ordersQuery);
      let totalOrderAmount = 0;
      let totalStickerAmount = 0;
      
      console.log('Found', snapshot.docs.length, 'T-shirt orders with matching franchiseeRef');
      
      if (snapshot.docs.length === 0) {
        console.log('No orders found with ref match, trying to get all orders...');
        const allOrdersSnapshot = await getDocs(collection(db, 'tshirtOrders'));
        console.log('Total orders in collection:', allOrdersSnapshot.docs.length);
        
        allOrdersSnapshot.docs.forEach(docSnap => {
          const order = docSnap.data();
          console.log('Order franchiseeRef:', order.franchiseeRef?.path);
          console.log('Looking for:', franchiseeRef.path);
          
          if (order.franchiseeRef?.path === franchiseeRef.path && order.includeInMonthEnd !== false) {
            console.log('Found matching order!');
            const orderDateValue = order.orderDate?.toDate ? order.orderDate.toDate() : 
                             order.orderDate ? new Date(order.orderDate) : null;
            
            if (orderDateValue) {
              const orderMonth = orderDateValue.getMonth();
              const orderYear = orderDateValue.getFullYear();
              
              if (orderMonth === selectedMonth && orderYear === selectedYear) {
                const stickerCost = order.stickers?.total || 0;
                const orderTotal = order.totalCost || order.totalCostToHO || 0;
                const shirtCost = orderTotal - stickerCost;
                totalOrderAmount += shirtCost;
                totalStickerAmount += stickerCost;
                console.log('Adding shirt amount:', shirtCost, 'sticker amount:', stickerCost);
              }
            }
          }
        });
      } else {
        snapshot.docs.forEach(docSnap => {
          const order = docSnap.data();
          
          if (order.includeInMonthEnd === false) {
            console.log('Skipping order - not included in month-end');
            return;
          }
          
          const orderDateValue = order.orderDate?.toDate ? order.orderDate.toDate() : 
                           order.orderDate ? new Date(order.orderDate) : null;
          
          if (orderDateValue) {
            const orderMonth = orderDateValue.getMonth();
            const orderYear = orderDateValue.getFullYear();
            
            if (orderMonth === selectedMonth && orderYear === selectedYear) {
              const stickerCost = order.stickers?.total || 0;
              const orderTotal = order.totalCost || order.totalCostToHO || 0;
              const shirtCost = orderTotal - stickerCost;
              totalOrderAmount += shirtCost;
              totalStickerAmount += stickerCost;
              console.log('Adding shirt amount:', shirtCost, 'sticker amount:', stickerCost);
            }
          }
        });
      }
      
      console.log('Total T-shirt orders for month:', totalOrderAmount);
      console.log('Total sticker orders for month:', totalStickerAmount);
      setTshirtOrders(totalOrderAmount);
      setStickerOrdersAmount(totalStickerAmount);
      
    } catch (error) {
      console.error('Error loading t-shirt orders:', error);
    }
  };

  const loadHOCharges = async (franchiseeId) => {
    try {
      const chargesQuery = query(
        collection(db, 'franchiseeCharges'),
        where('franchiseeId', '==', franchiseeId),
        where('month', '==', selectedMonth),
        where('year', '==', selectedYear),
        where('status', '==', 'pending')
      );
      
      const snapshot = await getDocs(chargesQuery);
      const charges = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setHoCharges(charges);
      
      let courierTotal = 0;
      let courierDescs = [];
      let otherTotal = 0;
      let otherDescs = [];
      
      charges.forEach(charge => {
        if (charge.type === 'courier') {
          courierTotal += charge.amount || 0;
          if (charge.description) courierDescs.push(charge.description);
        } else if (charge.type === 'other') {
          otherTotal += charge.amount || 0;
          if (charge.description) otherDescs.push(charge.description);
        }
      });
      
      if (courierTotal > 0) {
        setCourierFees(courierTotal);
        setCourierDescription(courierDescs.join('; '));
      }
      if (otherTotal > 0) {
        setOtherAmount(otherTotal);
        setOtherDescription(otherDescs.join('; '));
      }
      
    } catch (error) {
      console.error('Error loading HO charges:', error);
    }
  };

  const loadPreviousSubmissions = async (franchiseeRef) => {
    try {
      const submissionsQuery = query(
        collection(db, 'monthEndSubmissions'),
        where('franchiseeRef', '==', franchiseeRef),
        where('year', '==', selectedYear)
      );
      
      const snapshot = await getDocs(submissionsQuery);
      const submissions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPreviousSubmissions(submissions);
      return submissions;
    } catch (error) {
      console.error('Error loading previous submissions:', error);
      return [];
    }
  };

  const calculateRoyalties = (studentsList, submissions = []) => {
    let totalYearlyFees = 0;
    let termTotals = { term1: 0, term2: 0, term3: 0, term4: 0, admin: 0 };
    
    studentsList.forEach(student => {
      if (student.termBreakdown) {
        termTotals.term1 += student.termBreakdown.term1 || 0;
        termTotals.term2 += student.termBreakdown.term2 || 0;
        termTotals.term3 += student.termBreakdown.term3 || 0;
        termTotals.term4 += student.termBreakdown.term4 || 0;
        termTotals.admin += student.termBreakdown.admin || 0;
        
        totalYearlyFees += (student.termBreakdown.term1 || 0) +
                          (student.termBreakdown.term2 || 0) +
                          (student.termBreakdown.term3 || 0) +
                          (student.termBreakdown.term4 || 0);
      }
    });

    const royaltyBase = totalYearlyFees * 0.10;
    const vatRate = franchiseeInfo?.vatRate ?? 15;
    const vat = royaltyBase * (vatRate / 100);
    const totalRoyalty = royaltyBase + vat;

    let previousRoyaltiesPaid = 0;
    submissions.forEach(sub => {
      previousRoyaltiesPaid += sub.royaltyAmount || 0;
    });

    const amountDue = totalRoyalty - previousRoyaltiesPaid;

    setRoyaltyData({
      totalYearlyFees,
      termTotals,
      royaltyBase,
      vat,
      totalRoyalty,
      previousRoyaltiesPaid,
      amountDue: Math.max(0, amountDue)
    });
  };

  const calculateBrandBuildingFees = () => {
    let totalBBDue = 0;
    let totalBBPaid = 0;

    students.forEach(student => {
      if (student.totalAdminFee > 0) {
        totalBBDue += 20;
      }
    });

    previousSubmissions.forEach(sub => {
      totalBBPaid += sub.brandBuildingAmount || 0;
    });

    return {
      totalDue: totalBBDue,
      totalPaid: totalBBPaid,
      amountDue: Math.max(0, totalBBDue - totalBBPaid)
    };
  };

  const handleSubmitMonthEnd = async () => {
    if (!window.confirm(`Are you sure you want to submit the Month-End Return for ${monthNames[selectedMonth]} ${selectedYear}?\n\nThis will lock the submission and cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const bbFees = calculateBrandBuildingFees();
      const royaltyAccountPayment = (royaltyData.amountDue || 0) + 
                          (tshirtOrders || 0) + 
                          (stickerOrdersAmount || 0) +
                          (courierFees || 0) + 
                          (otherAmount || 0);

      const studentsForStorage = students.map(s => ({
        studentName: s.studentName || '',
        schoolName: s.schoolName || '',
        isActive: s.isActive,
        adminPaid: s.termBreakdown?.admin || 0,
        term1Paid: s.termBreakdown?.term1 || 0,
        term2Paid: s.termBreakdown?.term2 || 0,
        term3Paid: s.termBreakdown?.term3 || 0,
        term4Paid: s.termBreakdown?.term4 || 0
      }));

      const submission = {
        franchiseeRef,
        franchiseeName: franchiseeInfo?.name || '',
        month: selectedMonth,
        year: selectedYear,
        monthName: monthNames[selectedMonth],
        submissionDate: Timestamp.now(),
        totalYearlyFees: royaltyData.totalYearlyFees,
        termTotals: royaltyData.termTotals,
        royaltyBase: royaltyData.royaltyBase,
        vat: royaltyData.vat,
        totalRoyalty: royaltyData.totalRoyalty,
        previousRoyaltiesPaid: royaltyData.previousRoyaltiesPaid,
        royaltyAmount: royaltyData.amountDue,
        tshirtOrders,
        stickerOrdersAmount,
        courierFees,
        courierDescription,
        brandBuildingDue: bbFees.totalDue,
        brandBuildingPaid: bbFees.totalPaid,
        brandBuildingAmount: bbFees.amountDue,
        otherAmount,
        otherDescription,
        royaltyAccountPayment,
        brandBuildingPayment: bbFees.amountDue,
        totalStudents: students.length,
        activeStudents: students.filter(s => s.isActive).length,
        payingStudents: students.filter(s => s.totalTermFees > 0).length,
        students: studentsForStorage,
        isLocked: true,
        lockedBy: user.email,
        lockedAt: Timestamp.now()
      };

      await addDoc(collection(db, 'monthEndSubmissions'), submission);
      alert(`Month-End Return for ${monthNames[selectedMonth]} ${selectedYear} submitted successfully!`);
      await loadMonthEndData();
      
    } catch (error) {
      console.error('Error submitting month-end:', error);
      alert('Error submitting month-end: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const isMonthLocked = () => {
    return previousSubmissions.some(sub => 
      sub.month === selectedMonth && sub.year === selectedYear && sub.isLocked
    );
  };

  const getLockedSubmission = () => {
    return previousSubmissions.find(sub => 
      sub.month === selectedMonth && sub.year === selectedYear && sub.isLocked
    );
  };

  // ── CSV: Month-End Return ────────────────────────────────────────────────
  const exportMonthEndReturn = () => {
    let csv = 'BAF2: Month-End Return for:,Franchisee Name and Area:,' + (franchiseeInfo?.name || '') + '\n';
    csv += 'School,Student,Admin Fee,Term 1,Term 2,Term 3,Term 4,Total\n';
    
    students.forEach(student => {
      const tb = student.termBreakdown || {};
      const total = (tb.admin || 0) + (tb.term1 || 0) + (tb.term2 || 0) + (tb.term3 || 0) + (tb.term4 || 0);
      csv += `${student.schoolName || ''},${student.studentName || ''},`;
      csv += `R ${(tb.admin || 0).toFixed(2)},`;
      csv += `R ${(tb.term1 || 0).toFixed(2)},`;
      csv += `R ${(tb.term2 || 0).toFixed(2)},`;
      csv += `R ${(tb.term3 || 0).toFixed(2)},`;
      csv += `R ${(tb.term4 || 0).toFixed(2)},`;
      csv += `R ${total.toFixed(2)}\n`;
    });
    
    const tt = royaltyData?.termTotals || {};
    const grandTotal = (tt.admin || 0) + (tt.term1 || 0) + (tt.term2 || 0) + (tt.term3 || 0) + (tt.term4 || 0);
    csv += `\nTOTALS,,R ${(tt.admin || 0).toFixed(2)},`;
    csv += `R ${(tt.term1 || 0).toFixed(2)},`;
    csv += `R ${(tt.term2 || 0).toFixed(2)},`;
    csv += `R ${(tt.term3 || 0).toFixed(2)},`;
    csv += `R ${(tt.term4 || 0).toFixed(2)},`;
    csv += `R ${grandTotal.toFixed(2)}\n\n`;
    
    csv += `Total Term Fees (Royalty Base):,R ${royaltyData?.totalYearlyFees?.toFixed(2) || '0.00'}\n`;
    csv += `10% Royalty:,R ${royaltyData?.royaltyBase?.toFixed(2) || '0.00'}\n`;
    csv += `VAT (${franchiseeInfo?.vatRate ?? 15}%):,R ${royaltyData?.vat?.toFixed(2) || '0.00'}\n`;
    csv += `Total Royalty:,R ${royaltyData?.totalRoyalty?.toFixed(2) || '0.00'}\n`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MonthEndReturn_${monthNames[selectedMonth]}_${selectedYear}.csv`;
    a.click();
  };

  // ── CSV: Summary of Deposits ─────────────────────────────────────────────
  const exportSummaryOfDeposits = () => {
    const bbFees = calculateBrandBuildingFees();
    const totalPayment = (royaltyData?.amountDue || 0) + 
                        (tshirtOrders || 0) + 
                        (courierFees || 0) + 
                        (bbFees.amountDue || 0) + 
                        (otherAmount || 0);
    
    let csv = `SUMMARY OF DEPOSITS: ${selectedMonth + 1} ${monthNames[selectedMonth].toUpperCase()} ${selectedYear}\n\n`;
    csv += `Royalty ${selectedMonth + 1} ${monthNames[selectedMonth]}:,R ${royaltyData?.amountDue?.toFixed(2) || '0.00'}\n`;
    csv += `Orders,R ${tshirtOrders.toFixed(2)}\n`;
    csv += `Courier,R ${courierFees.toFixed(2)}\n`;
    csv += `Other,R ${otherAmount.toFixed(2)},${otherDescription}\n`;
    csv += `Total,R ${totalPayment.toFixed(2)}\n\n`;
    csv += 'To be paid into: LINDA LLOYD CC T/A BEAUX-ARTS KIDZ KRAFTZ: ABSA 730143311 BRANCH: 630510 REF: YOUR NAME AND AREA.\n\n';
    csv += `Admin Fee to pay ${selectedMonth + 1} ${monthNames[selectedMonth]},R ${bbFees.amountDue.toFixed(2)}\n`;
    csv += 'Admin Fee (R20\'s) paid into: LINDA LLOYD T/A BRANDBUILDING ABSA 730153082 BRANCH: 630510 REF: YOUR NAME AND AREA.\n\n';
    csv += `Total Schools:,${new Set(students.map(s => s.schoolName)).size}\n`;
    csv += `Total Students:,${students.length}\n`;
    csv += `Paying Students:,${students.filter(s => s.totalTermFees > 0).length}\n`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SummaryOfDeposits_${monthNames[selectedMonth]}_${selectedYear}.csv`;
    a.click();
  };

  // ── PDF: Summary of Deposits ─────────────────────────────────────────────
  const exportSummaryOfDepositsPDF = () => {
    const bbFees = calculateBrandBuildingFees();
    const locked = isMonthLocked();
    const lockedSubmission = locked ? getLockedSubmission() : null;

    // Use the same display values as the screen
    const dRoyalty     = locked && lockedSubmission ? lockedSubmission.royaltyAmount            : (royaltyData?.amountDue || 0);
    const dTshirts     = locked && lockedSubmission ? lockedSubmission.tshirtOrders             : tshirtOrders;
    const dStickers    = locked && lockedSubmission ? (lockedSubmission.stickerOrdersAmount || 0): stickerOrdersAmount;
    const dCourier     = locked && lockedSubmission ? lockedSubmission.courierFees              : courierFees;
    const dCourierDesc = locked && lockedSubmission ? lockedSubmission.courierDescription       : courierDescription;
    const dOther       = locked && lockedSubmission ? lockedSubmission.otherAmount              : otherAmount;
    const dOtherDesc   = locked && lockedSubmission ? lockedSubmission.otherDescription         : otherDescription;
    const dBB          = locked && lockedSubmission ? lockedSubmission.brandBuildingAmount      : bbFees.amountDue;
    const dRoyaltyTotal = locked && lockedSubmission
      ? lockedSubmission.royaltyAccountPayment
      : ((royaltyData?.amountDue || 0) + tshirtOrders + stickerOrdersAmount + courierFees + otherAmount);

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = 210;
    const margin = 18;
    const colRight = pageW - margin;
    let y = 20;

    const BLUE   = [43, 75, 160];
    const YELLOW = [255, 242, 0];
    const WHITE  = [255, 255, 255];
    const DARK   = [30, 30, 30];
    const LIGHT  = [245, 246, 250];
    const LINE   = [200, 205, 220];

    // Header bar
    pdf.setFillColor(...BLUE);
    pdf.rect(0, 0, pageW, 28, 'F');
    pdf.setFillColor(...YELLOW);
    pdf.rect(0, 28, pageW, 3, 'F');
    pdf.setTextColor(...WHITE);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BABS - Summary of Deposits', margin, 13);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      `${monthNames[selectedMonth].toUpperCase()} ${selectedYear}  |  ${franchiseeInfo?.name || ''}`,
      margin, 22
    );
    y = 40;

    // Helpers
    const drawRow = (label, value, bgColor, bold) => {
      if (bgColor) {
        pdf.setFillColor(...bgColor);
        pdf.rect(margin, y - 5, colRight - margin, 9, 'F');
      }
      pdf.setTextColor(...DARK);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.text(label, margin + 2, y);
      pdf.text(`R ${value}`, colRight - 2, y, { align: 'right' });
      y += 9;
    };

    const drawDivider = () => {
      pdf.setDrawColor(...LINE);
      pdf.line(margin, y - 3, colRight, y - 3);
    };

    const sectionHeader = (text) => {
      pdf.setFillColor(...BLUE);
      pdf.rect(margin, y - 5, colRight - margin, 8, 'F');
      pdf.setTextColor(...WHITE);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(text, margin + 2, y);
      y += 11;
    };

    const bankBox = (line1, line2, line3) => {
      y += 2;
      pdf.setDrawColor(...BLUE);
      pdf.setFillColor(235, 240, 255);
      pdf.roundedRect(margin, y, colRight - margin, 22, 2, 2, 'FD');
      pdf.setTextColor(...BLUE);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(line1, margin + 3, y + 6);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...DARK);
      pdf.text(line2, margin + 3, y + 12);
      pdf.text(line3, margin + 3, y + 18);
      y += 28;
    };

    // ROYALTY ACCOUNT section
    sectionHeader('ROYALTY ACCOUNT  (Acc: 730143311)');
    drawRow(`Royalty - ${monthNames[selectedMonth]} ${selectedYear}`, dRoyalty.toFixed(2));
    drawRow('T-Shirt Orders', dTshirts.toFixed(2));
    if (dStickers > 0) drawRow('Stickers', dStickers.toFixed(2));
    if (dCourier > 0)  drawRow(`Courier Fees${dCourierDesc ? ` (${dCourierDesc})` : ''}`, dCourier.toFixed(2));
    if (dOther > 0)    drawRow(`Other${dOtherDesc ? ` (${dOtherDesc})` : ''}`, dOther.toFixed(2));
    drawDivider();
    drawRow('TOTAL TO PAY INTO ROYALTY ACCOUNT', dRoyaltyTotal.toFixed(2), LIGHT, true);

    bankBox(
      'Pay into: LINDA LLOYD CC T/A BEAUX-ARTS KIDZ KRAFTZ',
      'Bank: ABSA  |  Account: 730143311  |  Branch: 630510',
      'Reference: YOUR NAME AND AREA'
    );

    // BRAND BUILDING section
    sectionHeader('BRAND BUILDING ACCOUNT  (Acc: 730153082)');
    drawRow(`Brand Building (R20 per student) - ${monthNames[selectedMonth]}`, dBB.toFixed(2));
    drawDivider();
    drawRow('TOTAL TO PAY INTO BRAND BUILDING ACCOUNT', dBB.toFixed(2), LIGHT, true);

    bankBox(
      'Pay into: LINDA LLOYD T/A BRANDBUILDING',
      'Bank: ABSA  |  Account: 730153082  |  Branch: 630510',
      'Reference: YOUR NAME AND AREA'
    );

    // Footer
    pdf.setDrawColor(...LINE);
    pdf.line(margin, y, colRight, y);
    pdf.setTextColor(130, 130, 130);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.text(
      `Generated by BABS on ${new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}`,
      pageW / 2, y + 5, { align: 'center' }
    );

    pdf.save(`SummaryOfDeposits_${monthNames[selectedMonth]}_${selectedYear}.pdf`);
  };

  // ── PDF: Month-End Return (student table) ────────────────────────────────
  const exportMonthEndReturnPDF = () => {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = 297;
    const margin = 12;

    const BLUE   = [43, 75, 160];
    const YELLOW = [255, 242, 0];
    const WHITE  = [255, 255, 255];

    // Header (first page)
    pdf.setFillColor(...BLUE);
    pdf.rect(0, 0, pageW, 22, 'F');
    pdf.setFillColor(...YELLOW);
    pdf.rect(0, 22, pageW, 2.5, 'F');
    pdf.setTextColor(...WHITE);
    pdf.setFontSize(15);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BABS - Month-End Return', margin, 10);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      `${monthNames[selectedMonth]} ${selectedYear}  |  ${franchiseeInfo?.name || ''}  |  Generated: ${new Date().toLocaleDateString('en-ZA')}`,
      margin, 18
    );

    // Table data
    const tt = royaltyData?.termTotals || {};
    const grandTotal = (tt.admin || 0) + (tt.term1 || 0) + (tt.term2 || 0) + (tt.term3 || 0) + (tt.term4 || 0);

    const tableBody = students.map(student => {
      const tb = student.termBreakdown || {};
      const rowTotal = (tb.admin || 0) + (tb.term1 || 0) + (tb.term2 || 0) + (tb.term3 || 0) + (tb.term4 || 0);
      return [
        student.schoolName || '',
        student.studentName || '',
        student.isActive ? 'Active' : 'Inactive',
        `R ${(tb.admin || 0).toFixed(2)}`,
        `R ${(tb.term1 || 0).toFixed(2)}`,
        `R ${(tb.term2 || 0).toFixed(2)}`,
        `R ${(tb.term3 || 0).toFixed(2)}`,
        `R ${(tb.term4 || 0).toFixed(2)}`,
        `R ${rowTotal.toFixed(2)}`,
      ];
    });

    // Totals row
    tableBody.push([
      'TOTALS', '', '',
      `R ${(tt.admin || 0).toFixed(2)}`,
      `R ${(tt.term1 || 0).toFixed(2)}`,
      `R ${(tt.term2 || 0).toFixed(2)}`,
      `R ${(tt.term3 || 0).toFixed(2)}`,
      `R ${(tt.term4 || 0).toFixed(2)}`,
      `R ${grandTotal.toFixed(2)}`,
    ]);

    autoTable(pdf, {
      startY: 29,
      margin: { left: margin, right: margin },
      head: [['School', 'Student', 'Status', 'Admin Fee', 'Term 1', 'Term 2', 'Term 3', 'Term 4', 'Total']],
      body: tableBody,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
      headStyles: { fillColor: BLUE, textColor: WHITE, fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { cellWidth: 52 },
        1: { cellWidth: 52 },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 24, halign: 'right' },
        4: { cellWidth: 24, halign: 'right' },
        5: { cellWidth: 24, halign: 'right' },
        6: { cellWidth: 24, halign: 'right' },
        7: { cellWidth: 24, halign: 'right' },
        8: { cellWidth: 27, halign: 'right', fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: [245, 246, 250] },
      didParseCell: (data) => {
        const isLastRow = data.row.index === tableBody.length - 1;
        if (isLastRow && data.section === 'body') {
          data.cell.styles.fillColor = BLUE;
          data.cell.styles.textColor = WHITE;
          data.cell.styles.fontStyle = 'bold';
        }
        if (!isLastRow && data.section === 'body') {
          const student = students[data.row.index];
          if (student && !student.isActive) {
            data.cell.styles.textColor = [150, 150, 150];
          }
        }
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) {
          pdf.setFillColor(...BLUE);
          pdf.rect(0, 0, pageW, 14, 'F');
          pdf.setFillColor(...YELLOW);
          pdf.rect(0, 14, pageW, 2, 'F');
          pdf.setTextColor(...WHITE);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text(
            `BABS - Month-End Return  (${monthNames[selectedMonth]} ${selectedYear} continued)`,
            margin, 10
          );
        }
        pdf.setFontSize(7);
        pdf.setTextColor(130, 130, 130);
        pdf.setFont('helvetica', 'italic');
        pdf.text(`Page ${data.pageNumber}`, pageW - margin, 205, { align: 'right' });
      }
    });

    pdf.save(`MonthEndReturn_${monthNames[selectedMonth]}_${selectedYear}.pdf`);
  };

  if (loading && students.length === 0) {
    return (
      <div className="month-end-container">
        <div className="loading-spinner">Loading month-end data...</div>
      </div>
    );
  }

  const locked = isMonthLocked();
  const lockedSubmission = locked ? getLockedSubmission() : null;
  const bbFees = calculateBrandBuildingFees();
  
  const displayRoyalty = locked && lockedSubmission ? lockedSubmission.royaltyAmount : (royaltyData?.amountDue || 0);
  const displayTshirts = locked && lockedSubmission ? lockedSubmission.tshirtOrders : tshirtOrders;
  const displayStickers = locked && lockedSubmission ? (lockedSubmission.stickerOrdersAmount || 0) : stickerOrdersAmount;
  const displayCourier = locked && lockedSubmission ? lockedSubmission.courierFees : courierFees;
  const displayCourierDesc = locked && lockedSubmission ? lockedSubmission.courierDescription : courierDescription;
  const displayOther = locked && lockedSubmission ? lockedSubmission.otherAmount : otherAmount;
  const displayOtherDesc = locked && lockedSubmission ? lockedSubmission.otherDescription : otherDescription;
  const displayBB = locked && lockedSubmission ? lockedSubmission.brandBuildingAmount : bbFees.amountDue;
  const displayRoyaltyTotal = locked && lockedSubmission ? lockedSubmission.royaltyAccountPayment : 
                      ((royaltyData?.amountDue || 0) + (tshirtOrders || 0) + (stickerOrdersAmount || 0) + (courierFees || 0) + (otherAmount || 0));
  
  const totalRoyaltyPayment = (royaltyData?.amountDue || 0) + 
                      (tshirtOrders || 0) + 
                      (stickerOrdersAmount || 0) +
                      (courierFees || 0) + 
                      (otherAmount || 0);

  return (
    <div className="month-end-container">
      {/* Header */}
      <div className="month-end-header">
        <h1>📊 Month-End Processing</h1>
      </div>

      {/* Month/Year Selection */}
      <div className="month-selection-card">
        <div className="selection-row">
          <label>Select Month:</label>
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {monthNames.map((name, index) => (
              <option key={index} value={index}>7 {name}</option>
            ))}
          </select>

          <label>Year:</label>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {[2024, 2025, 2026, 2027, 2028].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {locked && lockedSubmission && (
          <div className="locked-notice">
            <div>✅ This month was submitted on {lockedSubmission.submissionDate?.toDate ? 
              lockedSubmission.submissionDate.toDate().toLocaleDateString('en-ZA', { 
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
              }) : 'Unknown date'}</div>
            <div style={{ fontSize: '14px', marginTop: '4px', opacity: 0.9 }}>
              The submitted values are shown below for your reference when making payment.
            </div>
          </div>
        )}
      </div>

      {/* Royalty Summary */}
      {royaltyData && (
        <div className="royalty-summary-card">
          <h2>💰 Royalty Calculation for {monthNames[selectedMonth]} {selectedYear}</h2>
          
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Total Term Fees Received (Year to Date):</span>
              <span className="summary-value">R {royaltyData.totalYearlyFees.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Royalty Base (10%):</span>
              <span className="summary-value">R {royaltyData.royaltyBase.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">VAT ({franchiseeInfo?.vatRate ?? 15}%):</span>
              <span className="summary-value">R {royaltyData.vat.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Royalty (Year):</span>
              <span className="summary-value">R {royaltyData.totalRoyalty.toFixed(2)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Less: Previous Payments:</span>
              <span className="summary-value">R {royaltyData.previousRoyaltiesPaid.toFixed(2)}</span>
            </div>
            <div className="summary-item highlight">
              <span className="summary-label">Amount Due This Month:</span>
              <span className="summary-value">R {royaltyData.amountDue.toFixed(2)}</span>
            </div>
          </div>

          <div className="term-breakdown">
            <h3>Term Fees Received:</h3>
            <div className="term-grid">
              <div className="term-item">
                <span className="term-name">Admin Fees</span>
                <span className="term-amount">R {(royaltyData.termTotals?.admin || 0).toFixed(2)}</span>
              </div>
              <div className="term-item">
                <span className="term-name">Term 1</span>
                <span className="term-amount">R {(royaltyData.termTotals?.term1 || 0).toFixed(2)}</span>
              </div>
              <div className="term-item">
                <span className="term-name">Term 2</span>
                <span className="term-amount">R {(royaltyData.termTotals?.term2 || 0).toFixed(2)}</span>
              </div>
              <div className="term-item">
                <span className="term-name">Term 3</span>
                <span className="term-amount">R {(royaltyData.termTotals?.term3 || 0).toFixed(2)}</span>
              </div>
              <div className="term-item">
                <span className="term-name">Term 4</span>
                <span className="term-amount">R {(royaltyData.termTotals?.term4 || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary of Deposits */}
      <div className="sod-card">
        <h2>📊 Summary of Deposits {locked ? '(Submitted)' : ''}</h2>
        
        <div className="sod-items">
          <div className="sod-item">
            <span>Royalty {selectedMonth + 1} {monthNames[selectedMonth]}:</span>
            <span className="sod-amount">R {displayRoyalty.toFixed(2)}</span>
          </div>
          <div className="sod-item">
            <span>T-Shirt Orders:</span>
            <span className="sod-amount">R {displayTshirts.toFixed(2)}</span>
          </div>
          {displayStickers > 0 && (
            <div className="sod-item">
              <span>Stickers:</span>
              <span className="sod-amount">R {displayStickers.toFixed(2)}</span>
            </div>
          )}
          
          {locked ? (
            <>
              <div className="sod-item">
                <span>Courier Fees{displayCourierDesc ? ` (${displayCourierDesc})` : ''}:</span>
                <span className="sod-amount">R {displayCourier.toFixed(2)}</span>
              </div>
              <div className="sod-item">
                <span>Other{displayOtherDesc ? ` (${displayOtherDesc})` : ''}:</span>
                <span className="sod-amount">R {displayOther.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="sod-item editable">
                <span>Courier Fees:</span>
                <div className="fee-input-group">
                  <input 
                    type="text" 
                    value={courierDescription} 
                    onChange={(e) => setCourierDescription(e.target.value)}
                    disabled={locked}
                    placeholder="Description"
                    className="description-input"
                  />
                  <input 
                    type="number" 
                    value={courierFees} 
                    onChange={(e) => setCourierFees(parseFloat(e.target.value) || 0)}
                    disabled={locked}
                    placeholder="0.00"
                    className="amount-input"
                  />
                </div>
              </div>
              <div className="sod-item editable">
                <span>Other:</span>
                <div className="fee-input-group">
                  <input 
                    type="text" 
                    value={otherDescription} 
                    onChange={(e) => setOtherDescription(e.target.value)}
                    disabled={locked}
                    placeholder="Description"
                    className="description-input"
                  />
                  <input 
                    type="number" 
                    value={otherAmount} 
                    onChange={(e) => setOtherAmount(parseFloat(e.target.value) || 0)}
                    disabled={locked}
                    placeholder="0.00"
                    className="amount-input"
                  />
                </div>
              </div>
            </>
          )}
          
          <div className="sod-item total royalty-total">
            <span>TOTAL TO PAY INTO ROYALTY ACCOUNT (730143311):</span>
            <span className="sod-amount">R {displayRoyaltyTotal.toFixed(2)}</span>
          </div>
        </div>

        <div className="bank-details royalty-bank">
          <p><strong>Royalties & Orders paid into:</strong></p>
          <p>LINDA LLOYD CC T/A BEAUX-ARTS KIDZ KRAFTZ</p>
          <p>ABSA <strong>730143311</strong> | BRANCH: 630510</p>
          <p>REF: YOUR NAME AND AREA</p>
        </div>
        
        <div className="brandbuilding-section">
          <div className="sod-item total brandbuilding-total">
            <span>BRANDBUILDING (R20's) INTO ACCOUNT (730153082):</span>
            <span className="sod-amount">R {displayBB.toFixed(2)}</span>
          </div>
          <div className="bank-details brandbuilding-bank">
            <p><strong>BrandBuilding (R20's) paid into:</strong></p>
            <p>LINDA LLOYD T/A BRANDBUILDING</p>
            <p>ABSA <strong>730153082</strong> | BRANCH: 630510</p>
            <p>REF: YOUR NAME AND AREA</p>
          </div>
        </div>
      </div>

      {/* Student Statistics */}
      <div className="stats-card">
        <div className="stat">
          <span className="stat-label">Total Schools:</span>
          <span className="stat-value">{new Set(students.map(s => s.schoolName)).size}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Students:</span>
          <span className="stat-value">{students.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Students with Payments:</span>
          <span className="stat-value">{students.filter(s => s.totalTermFees > 0).length}</span>
        </div>
      </div>

      {/* Month-End Return Table */}
      <div className="month-end-return-table">
        <h2>📋 Month-End Return - All Students</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>School</th>
                <th>Student</th>
                <th>Status</th>
                <th>Admin Fee</th>
                <th>Term 1</th>
                <th>Term 2</th>
                <th>Term 3</th>
                <th>Term 4</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => {
                const tb = student.termBreakdown || {};
                const total = (tb.admin || 0) + (tb.term1 || 0) + (tb.term2 || 0) + (tb.term3 || 0) + (tb.term4 || 0);
                return (
                  <tr key={student.id} className={!student.isActive ? 'inactive-student' : ''}>
                    <td>{student.schoolName}</td>
                    <td>{student.studentName}</td>
                    <td>
                      <span className={`status-badge ${student.isActive ? 'active' : 'inactive'}`}>
                        {student.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>R {(tb.admin || 0).toFixed(2)}</td>
                    <td>R {(tb.term1 || 0).toFixed(2)}</td>
                    <td>R {(tb.term2 || 0).toFixed(2)}</td>
                    <td>R {(tb.term3 || 0).toFixed(2)}</td>
                    <td>R {(tb.term4 || 0).toFixed(2)}</td>
                    <td><strong>R {total.toFixed(2)}</strong></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals Summary */}
      <div className="totals-summary-card">
        <h2>📊 Month-End Totals</h2>
        <div className="totals-grid">
          <div className="total-item">
            <span className="total-label">Admin Fee</span>
            <span className="total-value">R {(royaltyData?.termTotals?.admin || 0).toFixed(2)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Term 1</span>
            <span className="total-value">R {(royaltyData?.termTotals?.term1 || 0).toFixed(2)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Term 2</span>
            <span className="total-value">R {(royaltyData?.termTotals?.term2 || 0).toFixed(2)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Term 3</span>
            <span className="total-value">R {(royaltyData?.termTotals?.term3 || 0).toFixed(2)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Term 4</span>
            <span className="total-value">R {(royaltyData?.termTotals?.term4 || 0).toFixed(2)}</span>
          </div>
          <div className="total-item grand-total">
            <span className="total-label">Grand Total</span>
            <span className="total-value">R {((royaltyData?.termTotals?.admin || 0) + (royaltyData?.totalYearlyFees || 0)).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">

        <button onClick={exportMonthEndReturn} className="export-button">
          📥 Export Month-End Return (CSV)
        </button>

        <button onClick={exportMonthEndReturnPDF} className="export-button pdf-button">
          📄 Download Month-End Return (PDF)
        </button>

        <button onClick={exportSummaryOfDeposits} className="export-button">
          📥 Export Summary of Deposits (CSV)
        </button>

        <button onClick={exportSummaryOfDepositsPDF} className="export-button pdf-button">
          📄 Download Summary of Deposits (PDF)
        </button>

        {!locked && (
          <button 
            onClick={handleSubmitMonthEnd} 
            className="submit-button"
            disabled={loading}
          >
            ✅ Submit Month-End Return
          </button>
        )}
      </div>
    </div>
  );
}

export default MonthEndProcessing;